import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Card from './Card';
import Button from './Button';
import { useGameStore } from '../stores/gameStore';

const getResultIndicatorClasses = (isCorrect) => {
	const baseClasses = 'w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white';
	const colorClass = isCorrect ? 'bg-success-500' : 'bg-danger-500';
	return `${baseClasses} ${colorClass}`;
};

export const RoundResults = ({ handleNextQuestion, handleViewResults, isHost }) => {
	const { t, i18n } = useTranslation();
	const roundResults = useGameStore((state) => state.roundResults);
	const isLastQuestion = roundResults?.isLastQuestion || false;
	const userLanguage = i18n.language;

	const sortedPlayerResults = useMemo(() => {
		if (!roundResults?.playerResults) return [];
		return [...roundResults.playerResults].sort((a, b) => b.points - a.points);
	}, [roundResults]);

	const buttonText = !isLastQuestion
		? t('roundResultsView.nextQuestionButton')
		: t('roundResultsView.viewFinalResultsButton');
	const buttonAction = !isLastQuestion ? handleNextQuestion : handleViewResults;

	const questionText = roundResults?.question?.questionTexts[userLanguage] || '';
	const correctAnswer = roundResults?.question?.correctAnswers[userLanguage] || '';

	if (!roundResults) {
		return <div className='container-game py-8'>{t('common.loadingMessage')}</div>;
	}

	return (
		<div className='container-game py-8'>
			<Card>
				<h2 className='text-2xl font-bold mb-4 text-center'>{t('roundResultsView.title')}</h2>

				<div className='mb-6 p-4 bg-gray-100 rounded-lg'>
					<p className='font-medium mb-2'>{t('roundResultsView.questionLabel')}</p>
					<p>{questionText}</p>
					<p className='mt-3 font-medium'>
						{t('roundResultsView.correctAnswerLabel')} <span className='text-success-600'>{correctAnswer}</span>
					</p>
				</div>

				<div className='mb-6'>
					<h3 className='text-lg font-semibold mb-3'>{t('roundResultsView.playerResultsTitle')}</h3>
					<div className='space-y-2'>
						{sortedPlayerResults.map(({ name, playerId, correct, points, totalScore, disconnected }) => (
							<div
								key={playerId}
								className={`flex items-center justify-between p-3 border rounded-lg ${
									disconnected ? 'opacity-50' : ''
								}`}
							>
								<div className='flex items-center'>
									<div className={getResultIndicatorClasses(correct)}>{correct ? '✓' : '✗'}</div>
									<span className='font-medium'>
										{name}
										{/* 2. Conditional Text: If the player is disconnected, we render a small span next to their name. */}
										{disconnected && (
											<span className='ml-2 text-xs font-semibold text-gray-800 uppercase'>
												({t('common.disconnectedStatus')})
											</span>
										)}
									</span>
								</div>
								<div className='text-right'>
									<div className='font-bold'>
										{points} {t('common.ptsSuffix')}
									</div>
									{!isLastQuestion && (
										<div className='text-sm text-gray-600'>
											{t('roundResultsView.totalScoreDisplay', { score: totalScore })}
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				</div>

				{isHost ? (
					<div className='text-center'>
						<Button variant='primary' onClick={buttonAction}>
							{buttonText}
						</Button>
					</div>
				) : (
					<div className='text-center text-gray-600'>{t('common.waitingForHostToProceedMessage')}</div>
				)}
			</Card>
		</div>
	);
};

RoundResults.propTypes = {
	handleNextQuestion: PropTypes.func.isRequired,
	handleViewResults: PropTypes.func.isRequired,
	isHost: PropTypes.bool.isRequired,
};
